import { TableRow } from "@mui/material";
import { useContext, useState } from "react";
import { Material } from "../../../../data/entities";
import { ROLE } from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import IconButton from "../../../../shared/components/IconButton";
import TextBox from "../../../../shared/components/TextBox";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import { formatCurrency, formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { error } from "../../../../utils/toast";
import { ProductButtonRow } from "../../../ProductOrder/single/Styled";
import { SummaryTableRow } from "../../../Provider/create/Content/MaterialList/AddMaterialModal/Styled";
import {
    CountBadge,
    LedgerSection,
    LedgerSectionHeader,
    LedgerSubtitle,
    LedgerTitle,
    LedgerTitleGroup,
    NumericCellText,
    TruncatedCellText,
} from "../../components";
import { StyledButton } from "../../Styled";
import { calculateProviderValue } from "../../utils";
import { AddMaterialModal } from "../AddMaterialModal";
import {
    EmptyMaterialHeader,
    EmptyMaterialSection,
    EmptyMaterialState,
    materialCellSx,
    materialTableContainerStyle,
} from "./Styled";

export const MaterialTableColumns = [
    { text: "Mã vật tư", icon: <></>, width: "112px", sx: { whiteSpace: "nowrap" } },
    { text: "Tên vật tư", icon: <></>, width: "320px" },
    {
        text: "Đơn vị",
        icon: <></>,
        width: "88px",
        sx: { whiteSpace: "nowrap", textAlign: "center", "& > div": { justifyContent: "center" } },
    },
    {
        text: "Giá gia công",
        icon: <></>,
        width: "150px",
        sx: { whiteSpace: "nowrap", textAlign: "center", "& > div": { justifyContent: "center" } },
    },
    { text: "Số lượng yêu cầu", icon: <></>, width: "150px", sx: { whiteSpace: "nowrap" } },
    { text: "", icon: <></>, width: "56px" },
];

interface MaterialRowProps {
    item: any;
    extraRowProps: any;
}

export const MaterialRow = ({ item, extraRowProps }: MaterialRowProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const canSeePrice = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);

    const quantity = item.selectedQuantity !== undefined ? item.selectedQuantity : 0;

    const provider_price = item.provider_price !== undefined ? formatCurrency(item.provider_price) : 0;

    return !item.isSummary ? (
        <TableRow>
            <StyledTableCell align="left" sx={materialCellSx.code}>
                <StyledNavLink to={`/materials/${item.id}`}>
                    {item.material_code ? item.material_code : item.id}
                </StyledNavLink>
            </StyledTableCell>
            <StyledTableCell align="left" sx={materialCellSx.name}>
                <TruncatedCellText title={item.name}>{item.name}</TruncatedCellText>
            </StyledTableCell>
            <StyledTableCell align="center" sx={materialCellSx.unit}>
                <TruncatedCellText title={item.measurement_unit}>{item.measurement_unit}</TruncatedCellText>
            </StyledTableCell>
            {canSeePrice && (
                <StyledTableCell align="center" sx={materialCellSx.price}>
                    <NumericCellText>{provider_price}</NumericCellText>
                </StyledTableCell>
            )}
            <StyledTableCell align="left" sx={materialCellSx.quantity}>
                <TextBox
                    numeric
                    style={{ width: "100%", boxSizing: "border-box" }}
                    text={formatInternationalNumber(quantity ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        extraRowProps.onChangeMaterialQuantity(item.id, n);
                    }}
                    disabled={extraRowProps.isView}
                />
            </StyledTableCell>
            <StyledTableCell align="center" sx={materialCellSx.action}>
                {!extraRowProps.isView && (
                    <IconButton onClick={() => extraRowProps.removeItem(item)}>
                        <DeleteIcon />
                    </IconButton>
                )}
            </StyledTableCell>
        </TableRow>
    ) : (
        <SummaryTableRow>
            <StyledTableCell align="left" sx={materialCellSx.code}>{item.id}</StyledTableCell>
            <StyledTableCell align="left" sx={materialCellSx.name}>
                <TruncatedCellText title={item.name}>{item.name}</TruncatedCellText>
            </StyledTableCell>
            <StyledTableCell align="center" sx={materialCellSx.unit}></StyledTableCell>
            {canSeePrice && (
                <StyledTableCell align="center" sx={materialCellSx.price}>
                    <NumericCellText>{formatCurrency(item.price)}</NumericCellText>
                </StyledTableCell>
            )}
            <StyledTableCell align="left" sx={materialCellSx.quantity}></StyledTableCell>
            <StyledTableCell align="left" sx={materialCellSx.action}></StyledTableCell>
        </SummaryTableRow>
    );
};

interface MaterialListProps {
    materials: any[];
    providerId?: string | number | null;
    providerName?: string;
    mergeOutsourceOrder: Function;
    isView?: boolean;
}

export const MaterialList = ({
    materials = [],
    providerId,
    providerName,
    mergeOutsourceOrder,
    isView = false,
}: MaterialListProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const canSeePrice = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);
    const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);

    const summaryMaterials = {
        id: `Tổng ${materials.length} vật tư`,
        name: "Subtotal",
        price: calculateProviderValue(materials),
        isSummary: true,
    };

    const tableItems = materials.length === 0 ? [] : [...materials, summaryMaterials];

    const onRemoveMaterial = (material: Material) => {
        const nextMaterials = materials.filter((item) => item.id !== material.id);
        mergeOutsourceOrder({ materials: nextMaterials });
    };

    const onChangeMaterialQuantity = (id: string, quantity: number) => {
        mergeOutsourceOrder({
            materials: materials.map((material) =>
                material.id === id ? { ...material, selectedQuantity: quantity } : material,
            ),
        });
    };

    const getColumn = () => {
        if (!canSeePrice) {
            return MaterialTableColumns.filter((column: any) => column.text !== "Giá gia công");
        }
        return MaterialTableColumns;
    };

    const openAddMaterialModal = () => {
        setIsMaterialModalOpen(true);
    };

    return (
        <>
            {isMaterialModalOpen && (
                <AddMaterialModal
                    selectedMaterials={materials}
                    providerId={providerId}
                    providerName={providerName}
                    open={isMaterialModalOpen}
                    handleClose={() => setIsMaterialModalOpen(false)}
                    mergeOutsourceOrder={mergeOutsourceOrder}
                />
            )}

            {materials.length === 0 ? (
                <EmptyMaterialSection>
                    <EmptyMaterialHeader>
                        <LedgerTitle>Vật tư gia công</LedgerTitle>
                    </EmptyMaterialHeader>
                    <EmptyMaterialState>
                        <div className="title">Chưa có vật tư gia công</div>
                        <div className="body">Thêm ít nhất một vật tư gia công để gửi đơn tới thủ kho.</div>
                    </EmptyMaterialState>
                    {!isView && (
                        <ProductButtonRow>
                            <StyledButton variant="contained" onClick={openAddMaterialModal}>
                                Thêm vật tư
                            </StyledButton>
                        </ProductButtonRow>
                    )}
                </EmptyMaterialSection>
            ) : (
                <LedgerSection>
                    <LedgerSectionHeader>
                        <LedgerTitleGroup>
                            <LedgerTitle>Vật tư gia công</LedgerTitle>
                            <LedgerSubtitle>Danh sách vật tư gia công và giá gia công.</LedgerSubtitle>
                        </LedgerTitleGroup>
                        <CountBadge>{materials.length}</CountBadge>
                    </LedgerSectionHeader>

                    <CustomTable
                        itemList={tableItems}
                        RowElement={MaterialRow}
                        TableColumns={getColumn()}
                        containerStyle={materialTableContainerStyle}
                        extraRowProps={{
                            removeItem: onRemoveMaterial,
                            onChangeMaterialQuantity,
                            providerName,
                            isView,
                        }}
                    />

                    {!isView && (
                        <ProductButtonRow>
                            <StyledButton variant="contained" onClick={openAddMaterialModal}>
                                Thêm vật tư
                            </StyledButton>
                        </ProductButtonRow>
                    )}
                </LedgerSection>
            )}
        </>
    );
};
